import React from 'react'
import { connect, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import Routes from '@src/routes'

import { setRoom } from '@store/actionSlices/appConfig'
import { setFirebaseSession } from '@store/actionSlices/firestore'
import { setProjectIdentity } from '@store/actionSlices/projectIdentity'
import { TokenInterface } from '@store/actionSlices/token'
import { DEFAULT_SHOWCASE_ROOM } from '@store/constants'
import type {
  ProjectIdentity,
  RootStateTypeExtra,
  SessionListContent,
} from '@store/types'

import IdleTimeHandler from '@components/idle-time-handler'

import {
  MasterKeyData,
  selectFromResult,
  useGetMasterKeyQuery,
} from '@api/master-key'
import { useLazyGetProjectListQuery } from '@api/project-list'
import { ShowcaseRoomConfig } from '@api/showcase-room'

import useConnectionIndicator from '@utilities/connection-indicator'
import { getQueryStringParams } from '@utilities/helper'
import RefreshHandler from '@utilities/refresh-handler'
import styleUtil from '@utilities/style-util'
import TokenRenewalHandler from '@utilities/token-renewal-handler'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import initFirebaseSnapshot from '@firebaseUtil/firebase-snapshot'
import { SessionMap } from '@firebaseUtil/types'

import '@adUtilities/sentry'
import { hasToken } from '@adUtilities/token-helper'

interface AppProps {
  session: SessionMap | undefined
  projectIdentity: ProjectIdentity
  token: TokenInterface
  isOnline: boolean
  remoteRefreshTriggerKey: string
  showcaseRoom?: string
  showcaseRoomConfig?: ShowcaseRoomConfig
}

const { REACT_APP_DATA_POLLING_INTERVAL_X_SECONDS } = process.env

function App({
  session,
  projectIdentity,
  token,
  isOnline,
  remoteRefreshTriggerKey,
  showcaseRoom,
  showcaseRoomConfig,
}: AppProps) {
  styleUtil(projectIdentity.theme)
  const location = useLocation()
  const dispatch = useDispatch()

  const { access_token } = getQueryStringParams(location.search)

  useConnectionIndicator({ token, isOnline })
  TokenRenewalHandler({ token })

  const firebaseSession = initFirebaseSnapshot(
    'devsuite',
    projectIdentity.projectId,
    'session',
    projectIdentity.sessionId
  )

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  React.useEffect(() => {
    if (showcaseRoom?.toLowerCase() === DEFAULT_SHOWCASE_ROOM) {
      dispatch(setRoom(''))
    }
  }, [])

  React.useEffect(() => {
    if (!access_token && firebaseSession) {
      dispatch(setFirebaseSession(firebaseSession as SessionMap))
    }
  }, [firebaseSession])

  const { masterKey, sessionId } = projectIdentity
  const { _id: userId } = token

  const skipMasterKeyQuery = React.useMemo(() => {
    const hasShowcaseRoomConfig = !!Object.keys(showcaseRoomConfig || {}).length

    if (!(hasToken(token) && masterKey && sessionId && hasShowcaseRoomConfig)) {
      return true
    }
    return false
  }, [token, masterKey, sessionId, showcaseRoomConfig])

  const masterKeyPayload = useGetMasterKeyQuery(
    {
      masterKey,
      userId,
    },
    {
      selectFromResult,
      skip: skipMasterKeyQuery,
      pollingInterval:
        Number(REACT_APP_DATA_POLLING_INTERVAL_X_SECONDS || 10) * 1000,
    }
  )

  const [getProjectListTrigger] = useLazyGetProjectListQuery()

  RefreshHandler({
    refreshTriggerKeyFirebase: session?.remoteRefreshTriggerKey ?? '',
    refreshTriggerKeyLocal: remoteRefreshTriggerKey,
  })

  const handleClientCheck = (client: boolean) => {
    if (!client) {
      firebaseControlQuery
        .update({
          [`client.remote`]: true,
        })
        .catch((err) => console.error('Client check encountered an error', err))
    }
  }

  const handleMasterSessionKeys = React.useCallback(
    async (argMasterKeyData: MasterKeyData) => {
      const { sessionKeys: masterSessionKeys } = argMasterKeyData
      if (masterSessionKeys.length < 1) {
        return
      }
      const filteredMasterSessionKeys = masterSessionKeys.filter(
        (item) =>
          !projectIdentity.disconnectedSessions?.find(
            (i) => i.projectId === item.projectId && i.sessionId === item.key
          )
      )

      const promise = await Promise.all(
        filteredMasterSessionKeys.map(async (sess) => {
          const response = await getProjectListTrigger({
            page: 1,
            query: sess.projectId.replace(/[^a-zA-Z0-9]/g, ' '),
          }).unwrap()

          const project = response?.data?.data?.[0]

          if (project) {
            await firebaseControlQuery.updateConnection({
              connected: true,
              projectId: sess.projectId,
              sessionId: sess.key,
            })
            return {
              projectId: sess.projectId,
              projectName: project.name,
              projectUUID: project.uuid,
              sessionId: sess.key,
              sessionName: '',
            }
          }
          return undefined
        })
      )

      const sessionList = promise.filter((res) => res)

      dispatch(
        setProjectIdentity({
          ...projectIdentity,
          ...showcaseRoomConfig,
          sessionList: sessionList as SessionListContent[],
        })
      )
    },
    [masterKeyPayload, showcaseRoomConfig]
  )

  React.useEffect(() => {
    const { masterKeyData, isError, status } = masterKeyPayload
    if (
      isError ||
      status !== 'fulfilled' ||
      !Object.keys(masterKeyData).length
    ) {
      return
    }
    handleMasterSessionKeys(masterKeyData)
  }, [handleMasterSessionKeys])

  React.useEffect(() => {
    if (session) {
      const {
        client: { remote },
      } = session
      handleClientCheck(remote)
    }
  }, [session])

  React.useEffect(() => {
    const queryParams = getQueryStringParams(location.search)
    if (!queryParams) return

    const updatedIdentity = {
      ...projectIdentity,
      disconnect:
        queryParams.disconnect !== undefined
          ? queryParams.disconnect === 'true'
          : projectIdentity.disconnect,
      showClearCacheOption:
        queryParams.clearCache !== undefined
          ? queryParams.clearCache === 'true'
          : projectIdentity.showClearCacheOption,
    }

    dispatch(setProjectIdentity(updatedIdentity))
  }, [])

  return (
    <>
      <Routes />
      <IdleTimeHandler />
    </>
  )
}

export default connect(
  ({
    projectIdentity,
    token,
    statusIndicator: { isOnline },
    firestore: { session },
    appConfig: { remoteRefreshTriggerKey, room: showcaseRoom },
    showcaseRoom: showcaseRoomConfig,
  }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
    token,
    isOnline,
    remoteRefreshTriggerKey,
    showcaseRoom,
    showcaseRoomConfig,
  })
)(App)
